import { useState } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { Button } from '../ui/button';
import { CenteredSpinner } from '../ui/Spinner';
import { listDriveFolder } from '../../modules/drive/api';
import { parseDriveFolderUrl } from '../../lib/parseDriveFolderUrl';
import { ApiError } from '../../lib/api';
import { DriveAssetGrid } from './DriveAssetGrid';
import { OrphanLabelPruneModal } from './OrphanLabelPruneModal';
import type { AssetFolderKind } from '../../lib/detectAssetLabelDefault';
import type { AssetLabel } from '../../types/campaign';

// Encapsulates: parse the folder URL → fetch the listing → render the grid
// (or the right empty/error state). Generalised in P2c Local Assets — the
// component is structurally identical for Meta assets (Campaign-level) and
// Local assets (row-level); callers pass `title` to label the surface.
//
// Discriminated union on `mode` so view callers don't need a no-op
// onChange prop. Both modes wire Refresh + Download All + lightbox; only
// edit mode lets the AM type into label inputs.

// Stage 1 §13.17. Discriminated union describing the prune target so
// the modal can be wired correctly. Optional — non-Meta + non-Local
// browsers (Google / Other folders) don't pass it and the button
// stays hidden.
type PruneTarget =
  | { kind: 'campaign'; campaignId: string; campaignName: string }
  | {
      kind: 'row';
      rolloutId: string;
      rowId: string;
      rowLabel: string;
      // OrganicAssets §9.3: which row-scoped surface this browser prunes.
      assetKind: 'local' | 'organic';
    };

type Props =
  | {
      mode: 'edit';
      folderUrl: string;
      value: AssetLabel[];
      onChange: (next: AssetLabel[]) => void;
      // Display title — drives the empty-state message and any caller-
      // facing copy. Defaults to "assets" for the lower-case substitution
      // used in inline messages.
      title?: string;
      // Ad Preview Mockups Stage 1 §2. Opt-in flag forwarded to
      // DriveAssetGrid: when true, each card renders the AdTypeDropdown
      // (edit) / AdTypeBadge (view). Callers that don't pass it get the
      // legacy card layout, so non-Meta browsers (Google / Other folders)
      // stay unaffected.
      showAdType?: boolean;
      // Stage 1 §13.15. Optional stub-detected callback forwarded to
      // DriveAssetGrid. Parent typically wires a fire-and-forget call
      // to the relevant server endpoint (Campaign or row local-assets
      // stubs). Only meaningful in edit mode + when showAdType is on.
      onStubsDetected?: (stubs: AssetLabel[]) => void;
      // Stage 1 §13.17. When supplied, the orphan-count line gains a
      // "Clear orphan labels" button that opens the prune modal.
      // Optional — surfaces that don't need pruning (e.g. read-only
      // contexts or one-off browsers) leave it undefined.
      pruneTarget?: PruneTarget;
      // Fires after a successful prune so the caller can refresh
      // upstream state (Campaign refetch, row refetch). Server has
      // already mutated; this is a notify-the-parent hook.
      onAfterPrune?: () => void;
      // OrganicAssets spec §8. Forwarded to DriveAssetGrid so auto-detected
      // stubs carry a default label text ("Image - Local - " …). Set by the
      // Local + Organic edit browsers only.
      labelKind?: AssetFolderKind;
    }
  | {
      mode: 'view';
      folderUrl: string;
      value: AssetLabel[];
      title?: string;
      showAdType?: boolean;
      // OrganicAssets §8 fix: view surfaces may opt into stub auto-creation
      // too (so Local/Organic default labels populate on render, not only in
      // the row editor's edit mode). When set, the grid fires the stub-write
      // — gated on the handler's presence, not on read-only. labelKind seeds
      // the default label text. Omitted by pure-display surfaces (Other /
      // Google folders, Campaign detail), so they stay write-free.
      onStubsDetected?: (stubs: AssetLabel[]) => void;
      labelKind?: AssetFolderKind;
    };

const FOLDER_QUERY_KEY = 'drive-folder';

export function DriveAssetsBrowser(props: Props) {
  const { mode, folderUrl, value, title, showAdType } = props;
  const folderId = parseDriveFolderUrl(folderUrl);
  const queryClient = useQueryClient();
  // Stage 1 §13.17. Modal open state. Hoisted here so the button + the
  // modal share lifecycle. Only ever opens in edit mode + when the
  // caller supplied a pruneTarget.
  const [pruneOpen, setPruneOpen] = useState(false);

  const listing = useQuery({
    queryKey: [FOLDER_QUERY_KEY, folderId],
    queryFn: () => listDriveFolder(folderId!),
    enabled: !!folderId,
    // Drive thumbnail URLs expire ~1h. Treat the cached listing as fresh
    // for 30 min, then refetch on next access — well within the 1h TTL on
    // the server. ?refresh=true forces a re-fetch and replaces the stored
    // listing (the server invalidates its own cache too).
    staleTime: 30 * 60 * 1000,
  });

  const trimmed = folderUrl.trim();
  const orphans = value.filter(
    (entry) =>
      !(listing.data?.files ?? []).some((file) => file.id === entry.driveFileId),
  );

  // Lower-case substitution used in inline messages ("Add a Drive folder
  // URL above to browse and label assets" → swap "assets" for the
  // caller's title where present).
  const lowerTitle = (title ?? 'assets').toLowerCase();

  // Empty URL: no folder configured at all. Don't render the grid; show
  // the inline hint below the URL field instead.
  if (!trimmed) {
    return (
      <p className="text-xs text-slate-500">
        {mode === 'edit'
          ? `Add a Drive folder URL above to browse and label ${lowerTitle}.`
          : `No ${lowerTitle} folder configured.`}
      </p>
    );
  }

  // URL set but doesn't parse — extracted to a dedicated message because
  // the input rejects nothing on its own (server validates length only).
  if (!folderId) {
    return (
      <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        Couldn't extract a folder id from this URL. Expected something like
        {' '}<code>drive.google.com/drive/folders/&lt;id&gt;</code>.
      </p>
    );
  }

  if (listing.isLoading) {
    return <CenteredSpinner />;
  }

  if (listing.isError) {
    const status = listing.error instanceof ApiError ? listing.error.status : null;
    let msg: string;
    if (status === 403) {
      msg =
        "Service account doesn't have access to this folder. Verify the folder is shared with the agency service account.";
    } else if (status === 404) {
      msg = 'Folder not found in Drive. Check the URL.';
    } else if (status === 429) {
      msg = 'Drive is rate limited right now. Try Refresh in a moment.';
    } else {
      msg = `Failed to load ${lowerTitle} from Drive.`;
    }
    return (
      <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        <p>{msg}</p>
        <button
          type="button"
          className="mt-1 text-red-700 underline"
          onClick={() => listing.refetch()}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!listing.data) return null;

  // Stage 1 §13.17. Show the prune button only when:
  //   - this is an edit-mode browser (no writes from view surfaces)
  //   - the caller supplied a pruneTarget (callers who don't want
  //     pruning, e.g. read-only contexts, simply omit it)
  //   - there's at least one orphan to prune (per user preference:
  //     even 1 orphan should be clearable)
  const pruneTarget = mode === 'edit' ? props.pruneTarget : undefined;
  const canPrune = pruneTarget !== undefined && orphans.length > 0;

  // Stage 1 §13.17. Post-prune cleanup: drop orphan entries from the
  // parent's `value` so ghost cards vanish immediately. Server already
  // wrote the pruned shape; the onChange fold-up keeps local form
  // state aligned without a Campaign refetch. Also invalidates the
  // Drive listing cache so a follow-up Drive change (e.g. a renamed
  // file) is picked up on next render, and fires the optional
  // onAfterPrune hook so the parent can refresh anything outside
  // this browser's scope (e.g. a rollout-row react-query key).
  function handlePruneSuccess() {
    setPruneOpen(false);
    if (mode === 'edit') {
      const liveFileIds = new Set(
        (listing.data?.files ?? []).map((f) => f.id),
      );
      const filtered = value.filter((entry) =>
        liveFileIds.has(entry.driveFileId),
      );
      if (filtered.length !== value.length) {
        props.onChange(filtered);
      }
    }
    queryClient.invalidateQueries({
      queryKey: [FOLDER_QUERY_KEY, folderId],
    });
    if (mode === 'edit') props.onAfterPrune?.();
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          {listing.data.files.length} file{listing.data.files.length === 1 ? '' : 's'}
          {orphans.length > 0
            ? ` · ${orphans.length} label${orphans.length === 1 ? '' : 's'} pointing at missing files`
            : ''}
          {listing.data.cached ? ' · cached' : ''}
        </p>
        <div className="flex items-center gap-2">
          {canPrune && (
            <Button
              variant="outline"
              type="button"
              onClick={() => setPruneOpen(true)}
            >
              Clear orphan labels
            </Button>
          )}
          <Button
            variant="outline"
            type="button"
            onClick={() => {
              window.open(folderUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            Download All
          </Button>
          <Button
            variant="outline"
            type="button"
            onClick={async () => {
              // Bypass the server cache (?refresh=true) and update RQ cache
              // with the fresh data. queryClient.invalidate would refetch via
              // the configured queryFn which doesn't pass refresh — handle
              // it manually for the explicit-refresh case.
              const fresh = await listDriveFolder(folderId, { refresh: true });
              queryClient.setQueryData([FOLDER_QUERY_KEY, folderId], fresh);
            }}
          >
            Refresh assets
          </Button>
        </div>
      </div>
      {pruneOpen && pruneTarget && mode === 'edit' && (
        <OrphanLabelPruneModal
          {...(pruneTarget.kind === 'campaign'
            ? {
                mode: 'campaign' as const,
                campaignId: pruneTarget.campaignId,
                campaignName: pruneTarget.campaignName,
                onConfirm: handlePruneSuccess,
              }
            : {
                mode: 'row' as const,
                rolloutId: pruneTarget.rolloutId,
                rowId: pruneTarget.rowId,
                rowLabel: pruneTarget.rowLabel,
                assetKind: pruneTarget.assetKind,
                onConfirm: handlePruneSuccess,
              })}
          orphanCount={orphans.length}
          onCancel={() => setPruneOpen(false)}
        />
      )}
      <DriveAssetGrid
        files={listing.data.files}
        value={value}
        // View-mode callers don't pass onChange; satisfy the grid's prop
        // shape with a no-op. The grid itself blocks edits via readOnly.
        onChange={mode === 'edit' ? props.onChange : () => {}}
        cached={listing.data.cached}
        readOnly={mode === 'view'}
        showAdType={showAdType}
        // OrganicAssets §8 fix: both edit AND view callers may opt into
        // stub auto-creation (the grid gates on the handler's presence, not
        // read-only). Both variants now declare these optional props, so
        // pass them through directly.
        onStubsDetected={props.onStubsDetected}
        // OrganicAssets §8: default label text for Local / Organic stubs.
        labelKind={props.labelKind}
      />
    </div>
  );
}
